import {Employee} from '../../models/employee/employee.model';
import {Store} from '@ngrx/store';
import {StandaloneState} from '../../store/standalone';
import {RxdbRepository} from '../rxdb.repository';
import {Inject, Injectable} from '@angular/core';
import {OKTA_AUTH} from '@okta/okta-angular';
import {OktaAuth} from '@okta/okta-auth-js';
import {getEmployee} from '../../store';
import {bearerFetch, logUpsertErrors, markPulled, shouldPull, withTabLock} from './multi-tab-sync';

const PULL_BATCH_SIZE = 10;

/**
 * Pull des opportunités CRM identique dans tous les onglets (pas de réplication RxDB liée au leader).
 */
@Injectable({
    providedIn: 'root'
})export class OpportunityCollectionSynchronizer{

    private readonly collectionName : string;
    private employee: Employee;

    public constructor( public readonly store: Store<StandaloneState>,
                        private rxdbRepository: RxdbRepository,
                        @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth) {
        this.collectionName = 'opportunities';
        this.store.select(getEmployee).subscribe(employee => {
            this.employee = employee;
        });

    }

    /**
     * mustWait : attendre un éventuel pull en cours dans un autre onglet et ignorer le throttle ;
     * sinon le pull est simplement sauté si un autre onglet s'en occupe.
     */
    async pull(mustWait: boolean): Promise<void> {
        try {
            await withTabLock(`epp-${this.collectionName}-pull`, async () => {
                if (!mustWait && !shouldPull(this.collectionName)) {
                    return;
                }
                const db = await this.rxdbRepository.initDb();
                const collection = db[this.collectionName];
                let page = 1;
                let hasMore = true;
                while (hasMore) {
                    const serverDocs = await this.fetchOpportunitiesPage(page);
                    if (serverDocs.length > 0) {
                        const newDocs = serverDocs.map(({ id, ...rest }) => ({ _id: id, ...rest }));
                        logUpsertErrors(this.collectionName, await collection.bulkUpsert(newDocs));
                    }
                    hasMore = serverDocs.length === PULL_BATCH_SIZE;
                    page++;
                }
                markPulled(this.collectionName);
            }, {ifAvailable: !mustWait});
        } catch (error) {
            console.warn(`Error during HTTP pull for [${this.collectionName}] :`, error.message);
        }
    }

    private async fetchOpportunitiesPage(page: number): Promise<any[]> {
        const dateThreeMonthsAgo = new Date();
        dateThreeMonthsAgo.setMonth(dateThreeMonthsAgo.getMonth() - 3);
        const formattedDate = dateThreeMonthsAgo.toISOString().split('T')[0];

        const queryParams = {
            employeeNumber: this.employee?.employeeNumber,
            statuses: 'P2_QUOTATION, P3_NEGOTIATION, P4_WAITING_SIGNATURE, P5_CLOSED_WON, P5_CLOSED_LOST',
            lastModifiedDate: formattedDate,
            page,
            size: PULL_BATCH_SIZE
        };

        const searchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (Array.isArray(value)) value.forEach(v => searchParams.append(key, String(v)));
            else if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });

        const url = '/apiQuoteManager/v2/opportunities/search?' + searchParams.toString();
        const response = await bearerFetch(url, this._oktaAuth, {method: 'GET'});
        const pageResponse = await response.json();
        return pageResponse?.content || [];
    }

}
